import React from 'react';
import PropTypes from 'prop-types';
import CalendarIcon from '../../assets/svg/components/calendar.svg';
import dayjs from 'dayjs';
import { useUsersByRole } from '../../hooks/useVehicleQuery';
import { usePhoneCountryCodes } from '../../hooks/userComplementaryQuery';
import SortDropdown from '../ui/dropdawn/SortDropdown';
import CustomDatePicker from '../ui/CustomDatePicker';
import CustomInputField from '../ui/inputs/CustomInputField';
import '../../assets/scss/components/vehicle/ContactSectionAddVehicle.scss';
import { useSearchablePagination } from '../../hooks/search-and-pagination/useSearchablePagination';

export default function ContactSectionAddVehicle({
  contactInfo,
  onContactInfoChange,
  theme,
  isTrailer = false,
  viewMode = "dual", // Añadido para soportar modo de vista
}) {
  const { data: countryCodes } = usePhoneCountryCodes();
  
  // Configuración de búsqueda para operadores (solo si NO es trailer)
  const operatorsSearch = useSearchablePagination(
    useUsersByRole,
    [5], // roleId para operadores
    {
      itemsPerPage: 5,
      enableSearch: true,
      enablePagination: true,
      enabled: !isTrailer
    }
  );
  
  // Configuración de búsqueda para propietarios
  const ownersSearch = useSearchablePagination(
    useUsersByRole,
    [6], // roleId para propietarios
    {
      itemsPerPage: 5,
      enableSearch: true,
      enablePagination: true
    }
  );
  
  const countryCodesData = countryCodes?.data || [];
  
  // Mapear datos para los dropdowns
  const operatorOptions = !isTrailer ? operatorsSearch.data.map((operator) => ({
    value: operator.id,
    label: `${operator.first_name} ${operator.first_last_name} ${operator.second_last_name || ''}`.trim(),
  })) : [];
  
  const ownerOptions = ownersSearch.data.map((owner) => ({
    value: owner.id,
    label: `${owner.first_name} ${owner.first_last_name} ${owner.second_last_name || ''}`.trim(),
  }));

  const countryOptions = !isTrailer ? countryCodesData.map((country) => ({
    value: country.id,
    label: `${country.country_code} (${country.country_name})`,
    country_code: country.country_code,
  })) : [];

  const nationalityOptions = countryCodesData.map((country) => ({
    value: country.id,
    label: country.country_name,
    country_code: country.country_code
  }));

  return (
    <div className={`GM__${theme}-contact-section ${viewMode}`}>
      <div className={`GM__${theme}-contact-header`}>
        <h2 className={`GM__${theme}-section-title`}>
          {isTrailer ? 'Información de Contacto del Trailer' : 'Información de Contacto'}
        </h2>
        <div className={`GM__${theme}-title-underline`}></div>
      </div>
      
      <div className={`GM__${theme}-contact-container`}>
        <div className={`GM__${theme}-contact-grid`}>
          
          {/* Tarjeta de personas */}
          <div className={`GM__${theme}-contact-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              {isTrailer ? 'Propietario' : 'Operador y Propietario'}
            </h3>
            <div className={`GM__${theme}-card-content`}>
              {/* Operador - Solo para vehículos principales */}
              {!isTrailer && (
                <div className={`GM__${theme}-input-group`}>
                  <label htmlFor="operatorId" className={`GM__${theme}-input-label`}>
                    Operador *
                  </label>
                  <SortDropdown
                    id="operatorId"
                    value={contactInfo.operatorId}
                    onChange={(value) => onContactInfoChange('operatorId', value)}
                    options={operatorOptions}
                    theme={theme}
                    placeholder="Seleccione un operador"
                    enableSearch={true}
                    enablePagination={true}
                    onSearch={operatorsSearch.handleSearch}
                    searchPlaceholder="Buscar operador..."
                    itemsPerPage={operatorsSearch.itemsPerPage}
                    totalItems={operatorsSearch.totalItems}
                    currentPage={operatorsSearch.currentPage}
                    onPageChange={operatorsSearch.handlePageChange}
                    searchTerm={operatorsSearch.searchTerm}
                    isLoading={operatorsSearch.isLoading}
                  />
                </div>
              )}

              {/* Propietario */}
              <div className={`GM__${theme}-input-group`}>
                <label htmlFor="ownerId" className={`GM__${theme}-input-label`}>
                  Propietario
                </label>
                <SortDropdown
                  id="ownerId"
                  value={contactInfo.ownerId}
                  onChange={(value) => onContactInfoChange('ownerId', value)}
                  options={ownerOptions}
                  theme={theme}
                  placeholder="Seleccione un propietario"
                  enableSearch={true}
                  enablePagination={true}
                  onSearch={ownersSearch.handleSearch}
                  searchPlaceholder="Buscar propietario..."
                  itemsPerPage={ownersSearch.itemsPerPage}
                  totalItems={ownersSearch.totalItems}
                  currentPage={ownersSearch.currentPage}
                  onPageChange={ownersSearch.handlePageChange}
                  searchTerm={ownersSearch.searchTerm}
                  isLoading={ownersSearch.isLoading}
                />
              </div>
            </div>
          </div>
          
          {/* Tarjeta de contrato */}
          <div className={`GM__${theme}-contact-card`}>
            <h3 className={`GM__${theme}-card-title`}>Información de Contrato</h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-input-group`}>
                <label htmlFor="contractStartDate" className={`GM__${theme}-input-label`}>
                  Fecha inicio contrato
                </label>
                <CustomDatePicker
                  id="contractStartDate"
                  value={contactInfo.contractStartDate ? dayjs(contactInfo.contractStartDate).toDate() : null}
                  onChange={(date) => onContactInfoChange("contractStartDate", date ? dayjs(date).format('YYYY-MM-DD') : null)}
                  icon={<img src={CalendarIcon} alt="calendar" />}
                  theme={theme}
                />
              </div>
              
              <div className={`GM__${theme}-input-group`}>
                <label htmlFor="contractEndDate" className={`GM__${theme}-input-label`}>
                  Fecha fin contrato
                </label>
                <CustomDatePicker
                  id="contractEndDate"
                  value={contactInfo.contractEndDate ? dayjs(contactInfo.contractEndDate).toDate() : null}
                  onChange={(date) => onContactInfoChange("contractEndDate", date ? dayjs(date).format('YYYY-MM-DD') : null)}
                  icon={<img src={CalendarIcon} alt="calendar" />}
                  theme={theme}
                />
              </div>
            </div>
          </div>
          
          {/* Tarjeta de teléfonos - Solo para vehículos principales */}
          {!isTrailer && (
            <div className={`GM__${theme}-contact-card`}>
              <h3 className={`GM__${theme}-card-title`}>Números de Contacto</h3>
              <div className={`GM__${theme}-card-content`}>
                <div className={`GM__${theme}-phone-group`}>
                  <label className={`GM__${theme}-input-label`}>
                    Número de Celular Principal *
                  </label>
                  <div className={`GM__${theme}-phone-input-container`}>
                    <SortDropdown
                      id="primaryPhoneCountry"
                      value={contactInfo.primaryPhoneCountryId || ""}
                      onChange={(value) => onContactInfoChange('primaryPhoneCountryId', value)}
                      options={countryOptions}
                      type="phone"
                      theme={theme}
                      placeholder="Código"
                    />
                    <CustomInputField
                      id="primaryPhone"
                      type="tel"
                      name="primaryPhoneNumber"
                      value={contactInfo.primaryPhoneNumber}
                      onChange={(e) => onContactInfoChange('primaryPhoneNumber', e.target.value)}
                      placeholder="Número"
                      theme={theme}
                    />
                  </div>
                </div>

                <div className={`GM__${theme}-phone-group`}>
                  <label className={`GM__${theme}-input-label`}>
                    Número de Celular Secundario
                  </label>
                  <div className={`GM__${theme}-phone-input-container`}>
                    <SortDropdown
                      id="secondaryPhoneCountry"
                      value={contactInfo.secondaryPhoneCountryId || ""}
                      onChange={(value) => onContactInfoChange('secondaryPhoneCountryId', value)}
                      options={countryOptions}
                      type="phone"
                      theme={theme}
                      placeholder="Código"
                    />
                    <CustomInputField
                      id="secondaryPhone"
                      type="tel"
                      name="secondaryPhoneNumber"
                      value={contactInfo.secondaryPhoneNumber}
                      onChange={(e) => onContactInfoChange('secondaryPhoneNumber', e.target.value)}
                      placeholder="Número"
                      theme={theme}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tarjeta de nacionalidad */}
          <div className={`GM__${theme}-contact-card ${isTrailer ? 'full-width' : ''}`}>
            <h3 className={`GM__${theme}-card-title`}>Información Adicional</h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-input-group`}>
                <label htmlFor="nationalityId" className={`GM__${theme}-input-label`}>
                  Nacionalidad *
                </label>
                <SortDropdown
                  id="nationalityId"
                  value={contactInfo.nationalityId || ""}
                  onChange={(value) => onContactInfoChange('nationalityId', value)}
                  options={nationalityOptions}
                  type="nationality"
                  theme={theme}
                  placeholder="Seleccione país"
                />
              </div>
              
              {/* Información para trailers */}
              {isTrailer && (
                <div className={`GM__${theme}-info-notice`}>
                  <div className={`GM__${theme}-info-icon`}>ℹ</div>
                  <div className={`GM__${theme}-info-text`}>
                    Los trailers no requieren operador ni números de contacto propios, 
                    ya que utilizan la información del cabezote al cual están vinculados.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ContactSectionAddVehicle.propTypes = {
  contactInfo: PropTypes.shape({
    operatorId: PropTypes.string,
    ownerId: PropTypes.string,
    primaryPhoneNumber: PropTypes.string,
    primaryPhoneCountryId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    secondaryPhoneNumber: PropTypes.string,
    secondaryPhoneCountryId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    contractStartDate: PropTypes.string,
    contractEndDate: PropTypes.string,
    nationalityId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ])
  }).isRequired,
  onContactInfoChange: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  isTrailer: PropTypes.bool,
  viewMode: PropTypes.oneOf(['simple', 'dual']),
};